import React, {useEffect, useState} from 'react';
import Config from "../../core/config";
import {useRecoilState} from "recoil";
import {loadingAtom} from "../../atoms";
import Cookies from "js-cookie";
import {useNavigate} from "react-router-dom";
import ActionButtonsManagerView from "../common/ActionButtonsManagerView";
import ConfirmButton from "../common/ConfirmButton";
import BackButton from "../common/BackButton";
import {makeRequest} from "../../utils/httpRequest";
import {closeOrder, getAllPaymentMethods, getCompanyByUserUUID, insertSale, updateSale} from "../../utils/requestUtils";

const apiUrl = Config.apiUrl;

const SalePaymentMethods = () => {
    const navigate = useNavigate();
    const [, setLoading] = useRecoilState(loadingAtom);
    const [closingData, setClosingData] = useState(null);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [multiplePaymentMethodsEnabled, setMultiplePaymentMethodsEnabled] = useState(false);
    const [paymentMethodValues, setPaymentMethodValues] = useState([
        {paymentMethod: {id: undefined}, value: 0}
    ]);
    const [typedTotalValue, setTypedTotalValue] = useState(0);

    useEffect(() => {
        const stored = localStorage.getItem('saleClosing');
        if (!stored) {
            navigate('/app');
            return;
        }
        const parsed = JSON.parse(stored);
        setClosingData(parsed);
        setPaymentMethodValues(
            parsed.existingPayments && parsed.existingPayments.length > 0
                ? parsed.existingPayments
                : [{paymentMethod: {id: undefined}, value: parsed.totalValue}]
        );
        getAllPaymentMethods().then((p) => setPaymentMethods(p || []));
        getCompanyByUserUUID().then((company) => {
            if (company) {
                setMultiplePaymentMethodsEnabled(!!company.multiplePaymentMethodsEnabled);
            }
        });
    }, []);

    function handlePaymentMethodSelected(index, id) {
        if (id) {
            const paymentMethod = paymentMethods.find((p) => p.id === Number(id));
            setPaymentMethodValues((prevItems) =>
                prevItems.map((item, i) => i === index ? {...item, paymentMethod} : item)
            );
        }
    }

    function handleValueChange(index, value) {
        const parsedValue = parseFloat(value);
        setPaymentMethodValues((prevItems) =>
            prevItems.map((item, i) => i === index ? {
                ...item,
                value: Number.isNaN(parsedValue) ? 0 : parsedValue
            } : item)
        );
    }

    function handleSplitsChange(index, value) {
        setPaymentMethodValues((prevItems) =>
            prevItems.map((item, i) => i === index ? {...item, splits: Number(value)} : item)
        );
    }

    useEffect(() => {
        if (!closingData) {
            return;
        }
        let sum = 0;
        paymentMethodValues.forEach((p) => sum += p.value);
        setTypedTotalValue(sum);

        if (multiplePaymentMethodsEnabled && sum > 0 && sum < closingData.totalValue && paymentMethodValues[paymentMethodValues.length - 1].value > 0) {
            setPaymentMethodValues((prevItems) => [...prevItems, {paymentMethod: {id: undefined}, value: 0}]);
        }

        if (sum === closingData.totalValue) {
            setPaymentMethodValues((prevItems) => prevItems.filter((item) => item.value > 0));
        }
    }, [paymentMethodValues]);

    const doConfirm = async () => {
        if (Math.abs(closingData.totalValue - typedTotalValue) > 0.01) {
            alert(`O total informado (R$ ${typedTotalValue.toFixed(2)}) precisa ser igual ao total a fechar (R$ ${closingData.totalValue.toFixed(2)}).`);
            return;
        }

        if (paymentMethodValues.find((p) => p.value > 0 && (p.paymentMethod === undefined || p.paymentMethod.id === undefined))) {
            alert('Informe uma forma de pagamento.');
            return;
        }

        /* eslint-disable no-restricted-globals */
        if (!confirm('Essa operação não poderá ser revertida. Deseja confinuar?')) {
            return;
        }

        setLoading(true);
        try {
            const userUUID = Cookies.get('userUUID');

            const payments = paymentMethodValues.filter((p) => p.value > 0).map((p) => ({
                paymentMethodId: p.paymentMethod.id,
                value: p.value
            }));

            if (closingData.mode === 'balcao' || closingData.mode === 'delivery') {
                await insertSale({...closingData.sale, payments});
                localStorage.removeItem('saleClosing');
                navigate('/app');
                return;
            }

            if (closingData.mode === 'edit') {
                await updateSale({...closingData.sale, payments});
                localStorage.removeItem('saleClosing');
                navigate('/sale/sales');
                return;
            }

            if (closingData.mode === 'table') {
                await closeOrder(closingData.salesId, {...closingData.sale, payments});
                localStorage.removeItem('saleClosing');
                navigate('/app');
                return;
            }

            const urlReceipt = apiUrl + '/api/receipts/list';
            await makeRequest(urlReceipt, 'POST', {
                'Content-Type': 'application/json',
                'Useruuid': userUUID,
            }, JSON.stringify(closingData.checkedCustomers));

            await closeOrder(closingData.salesId, {...closingData.sale, payments});

            localStorage.removeItem('saleClosing');

            let urlToGo = '/app';
            if (closingData.customersUnchecked.length > 0) {
                const idsString = closingData.customersUnchecked.map((c) => c.id).join(',');
                urlToGo = `/sale/finishitems/${idsString}`;
            }
            navigate(urlToGo);
        } catch (error) {
            console.error('Error closing sale:', error);
            alert('Não foi possível fechar a comanda.');
        } finally {
            setLoading(false);
        }
    };

    if (!closingData) {
        return null;
    }

    return (
        <>
            <div className="header-label">Formas de Pagamento</div>

            {closingData.sale?.deliveryFee > 0 &&
                <div style={{marginTop: '5px', textAlign: 'right'}}>
                    Entrega: R$ {closingData.sale.deliveryFee.toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                })}
                </div>
            }

            <div style={{marginTop: '5px', alignItems: 'center'}}>
                <div className="total-label" style={{textAlign: 'right', fontSize: '30px', color: '#cc5545'}}>
                    Valor Total: R$ {closingData.totalValue.toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                })}
                </div>
            </div>

            <table style={{width: '100%'}}>
                <thead>
                <tr>
                    <th>Forma de Pagamento</th>
                    <th>Valor</th>
                    <th>{paymentMethodValues.find((p) => p.paymentMethod.allowSplit === true) && "Parcelas"}</th>
                </tr>
                </thead>
                <tbody>
                {paymentMethodValues.map((paymentMethodValue, index) => (
                    <tr key={index}>
                        <td>
                            <select style={{width: '100%', maxWidth: '220px'}}
                                    value={paymentMethodValue.paymentMethod.id ?? ''}
                                    onChange={(e) => handlePaymentMethodSelected(index, e.target.value)}>
                                <option value="">Selecione uma Forma de Pagamento</option>
                                {paymentMethods.map((paymentMethod, i) => (
                                    <option key={i} value={paymentMethod.id}>
                                        {paymentMethod.name}
                                    </option>
                                ))}
                            </select>
                        </td>
                        <td>
                            <input style={{width: '80px'}}
                                   type="number" step="0.01" placeholder="0.00"
                                   value={paymentMethodValue.value}
                                   onChange={(e) => handleValueChange(index, e.target.value)}/>
                        </td>
                        <td>
                            {paymentMethodValue.paymentMethod.allowSplit && (
                                <input type="number"
                                       style={{width: '80px'}}
                                       step="1" placeholder="1"
                                       value={paymentMethodValue.splits}
                                       onChange={(e) => handleSplitsChange(index, e.target.value)}/>
                            )}
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>

            <ActionButtonsManagerView>
                <BackButton onClick={() => navigate(-1)}/>
                <ConfirmButton onClick={doConfirm}/>
            </ActionButtonsManagerView>
        </>
    );
};

export default SalePaymentMethods;
